import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, TruckIcon } from 'lucide-react';
import { requestOffer } from '../../utils/offerBus';
import { onBuilderStepRequest } from '../../utils/deepLinks';
import { BUILDER_ID, builderPrices, builderSteps, essentialIncludes } from '../../data/builder';
import { SHIPPING_LINE, WARRANTY_LINE } from '../../data/product';
import { useBuilder } from '../../hooks/useBuilder';
import { useElementInView } from '../../hooks/useElementInView';
import { useTrackOnView } from '../../hooks/useTrackOnView';
import { money } from '../QuantityStepper';
import { BuilderVisual } from './BuilderVisual';
import { BuilderStepPanel } from './BuilderStepPanel';
import { BuilderSummary, BuilderSummaryBar } from './BuilderSummary';
import { CompleteUpgradeNotice } from './CompleteUpgradeNotice';
import { HardCaseDownsell } from './HardCaseDownsell';
import { UspsBadge } from '../UspsBadge';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
import { ReviewsStrip } from '../BannerOverlays';
export function BuildYourX1() {
  const builder = useBuilder();
  const reduce = useReducedMotion();
  const viewRef = useTrackOnView<HTMLElement>('build_your_x1_view');
  const inView = useElementInView(BUILDER_ID, '-30% 0px -30% 0px');
  const {
    state,
    step
  } = builder;
  const index = builderSteps.findIndex(item => item.id === step);
  const [pulse, setPulse] = useState(0);

  // Any "Build your X1" link: let the smooth scroll land, then briefly highlight the builder.
  useEffect(() => {
    let timer = 0;
    const onClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement | null)?.closest('a');
      const href = link?.getAttribute('href');
      if (!link || href !== `#${BUILDER_ID}` && href !== '#offer') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(count => count + 1), 750);
    };
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      window.clearTimeout(timer);
    };
  }, []);

  // Deep links and section buttons (#build, "Add RX to my build") open a specific step.
  const goToStepRef = useRef(builder.goToStep);
  goToStepRef.current = builder.goToStep;
  useEffect(() => {
    let timer = 0;
    const off = onBuilderStepRequest(next => {
      goToStepRef.current(next);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(count => count + 1), 750);
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);
  const stepDone = (id: string) => id === 'lens' && step !== 'lens' || id === 'lensSystem' && (state.fullLens || state.complete) || id === 'case' && (state.hardCase || state.complete) || id === 'care' && state.careKit || id === 'rx' && state.rx > 0 || id === 'protection' && state.protection !== 'basic';
  return <section ref={viewRef} id={BUILDER_ID} data-builder="true" aria-labelledby="builder-heading" className="relative scroll-mt-24 border-b border-white/10 bg-ink-950" data-id="element-276" data-mp="2s845rb6dmnhl">
      {pulse > 0 && <motion.span key={pulse} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 border-2 border-grit-500" initial={{
      opacity: 0
    }} animate={{
      opacity: reduce ? 0 : [0, 1, 0]
    }} transition={{
      duration: 0.3,
      times: [0, 0.35, 1],
      ease: 'easeOut'
    }} data-id="element-277" data-mp="1gnvv0j6y2sd7" />}
      <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16" data-id="element-278" data-mp="sz2usajahycs">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between" data-id="element-279" data-mp="xx4e8wkmnzos">
          <div data-id="element-280" data-mp="12po00gtgy14o">
            <ReviewsStrip className="!mx-0 mb-2 !px-0" data-id="element-281" data-mp="12q77f3lht74g" />
            <p className="font-display text-xl font-extrabold uppercase tracking-wide text-grit-500" data-id="element-282" data-mp="59qnxhbfq2dn">
              Your lens. Your setup. Your way.
            </p>
            <h2 id="builder-heading" className="grit-headline mt-1 text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-283" data-mp="a68sag2urypg">
              Build your <span className="text-grit-500" data-id="element-284" data-mp="27wxcgbyqn630">X1.</span>
            </h2>
            <p className="mt-3 text-lg font-medium text-steel-300" data-id="element-285" data-mp="2ln0tmoirg9ll">
              Choose your lens. Add only what you need. Watch it come together.
            </p>
            <a href="#pack-single" onClick={event => {
            event.preventDefault();
            requestOffer('single');
          }} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 hover:text-grit-500 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-286" data-mp="2gv98wumacy3d">
              Want a ready-made kit? Complete Systems &amp; deals below
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-287" data-mp="1a9ymsl6gqyem" />
            </a>
          </div>
          <div className="lg:text-right" data-id="element-288" data-mp="15bf20o3lvsnu">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-289" data-mp="3gw4gn1jeu0vy">GritVue X1 Essential</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-none text-offwhite" data-id="element-290" data-mp="3lu5y5i16ts5b">
              Starting at <span className="text-grit-500" data-id="element-291" data-mp="383u4vfsv9ncl">{money(builderPrices.essential)}</span>
            </p>
            <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-steel-300 lg:justify-end" data-id="element-292" data-mp="3c3uuzxpbvmzh">
              <TruckIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-293" data-mp="334pwushohvnu" />
              {SHIPPING_LINE}
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10" data-id="element-294" data-mp="2p718vbsbe8ek">
          {/* The product stays anchored in view while the build changes it */}
          <div className="sticky top-[5.25rem] z-20 -mx-4 self-start bg-ink-950 px-4 pb-3 sm:-mx-6 sm:px-6 lg:top-24 lg:mx-0 lg:px-0 lg:pb-0" data-id="element-295" data-mp="ndlhgcoekpij">
            <BuilderVisual state={state} animateLenses={builder.step === 'lensSystem'} data-id="element-296" data-mp="renhuwu237m3" />
            <ul className="mt-4 hidden gap-x-6 gap-y-1.5 sm:grid sm:grid-cols-3" data-id="element-297" data-mp="20dyfmm8rq9m6">
              {essentialIncludes.map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-steel-400" data-id="element-298" data-mp="1rbp7u44vwuch">
                  <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-299" data-mp="1rdjqfbsooade" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div id="builder-panel" className="grid scroll-mt-[26rem] content-start gap-4 lg:scroll-mt-28" data-id="element-300" data-mp="2j4pl3cpmxfva">
            {/* Mobile: one readable step label and a thin progress bar */}
            <div className="lg:hidden" aria-live="polite" data-id="element-301" data-mp="2a8gmg09hela7">
              <p className="flex items-baseline justify-between text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-302" data-mp="25af4jinsks0b">
                <span data-id="element-303" data-mp="32s6s75v7j6ir">
                  Step {index + 1} of {builderSteps.length}
                </span>
                <span className="font-display text-lg tracking-wide text-offwhite" data-id="element-304" data-mp="2xy2kgke4augm">{builderSteps[index]?.label}</span>
              </p>
              <div className="mt-2 h-1 w-full bg-white/10" data-id="element-305" data-mp="3aa4wmvyj7rf4">
                <motion.div className="h-full origin-left bg-grit-500" animate={{
                scaleX: (index + 1) / builderSteps.length
              }} transition={{
                duration: reduce ? 0 : 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-306" data-mp="3aan12fvclby4" />
              </div>
            </div>

            {/* Desktop: full step navigation */}
            <nav aria-label="Build steps" className="hidden lg:block" data-id="element-307" data-mp="3t8h8ipgtvfee">
              <ol className="grid grid-cols-7 border border-white/10" data-id="element-308" data-mp="1uld4onnxnd9">
                {builderSteps.map((item, i) => {
                const current = item.id === step;
                const done = stepDone(item.id);
                return <li key={item.id} className={i > 0 ? 'border-l border-white/10' : ''} data-id="element-309" data-mp="awuil042qqjs" data-mpi={i}>
                      <button type="button" onClick={() => builder.goToStep(item.id)} aria-current={current ? 'step' : undefined} className={`flex h-full w-full flex-col items-center gap-1 px-1 py-2.5 transition-colors duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grit-500 ${current ? 'bg-grit-500 text-ink-950' : 'text-steel-300 hover:bg-white/[0.04] hover:text-offwhite'}`} data-id="element-310" data-mp="fv4h9kp0c9a5">
                        <span className={`flex h-5 w-5 items-center justify-center text-[11px] font-extrabold ${current ? '' : done ? 'bg-grit-500 text-ink-950' : 'border border-white/25'}`} data-id="element-311" data-mp="dha099bue7pz">
                          {done && !current ? <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-312" data-mp="3vrlor5imi0wn" /> : i + 1}
                        </span>
                        <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-313" data-mp="26c2jx2crtooy">{item.label}</span>
                      </button>
                    </li>;
              })}
              </ol>
            </nav>

            <CompleteUpgradeNotice hint={builder.hint} complete={state.complete} onUpgrade={builder.upgradeToComplete} onRevert={builder.revertToEssential} data-id="element-314" data-mp="1wg7v1phyexdq" />

            <BuilderStepPanel builder={builder} data-id="element-315" data-mp="2u256j2xx22g6" />

            {step !== 'review' && <>
                <div className="hidden lg:block" data-id="element-316" data-mp="2g2npj9qv3thd">
                  <BuilderSummary builder={builder} data-id="element-317" data-mp="2g1ue3vgykfsw" />
                </div>
                {/* Phones: the summary lives in the bottom bar, so show shipping + payment trust here */}
                <div className="border border-white/10 bg-ink-900 p-4 lg:hidden" data-id="element-318" data-mp="14m5wljmh48zh">
                  <UspsBadge note="Free U.S. shipping." data-id="element-319" data-mp="18p2grernwd5j" />
                  <SecureCheckoutNote className="mt-4" data-id="element-320" data-mp="18mhckj3w5l4m" />
                </div>
              </>}
          </div>
        </div>

        {/* Keep people scrolling: the ready-made kits and pack deals are right below */}
        <div className="mt-12 flex flex-col gap-5 border-t-4 border-grit-500 bg-ink-900 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between" data-id="element-321" data-mp="ewtvjgm4fp4q">
          <div data-id="element-322" data-mp="iwm1dlk4rh6e">
            <p className="font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-323" data-mp="iw2km867uyty">
              Don&apos;t want to build it? <span className="text-grit-500" data-id="element-324" data-mp="9xqxwh8h0l6s">Keep scrolling.</span>
            </p>
            <p className="mt-3 max-w-2xl text-[15px] text-steel-300" data-id="element-325" data-mp="3s3df321eordb">
              Ready-made Complete Systems with all 3 lenses and the hard case, plus multi-pack deals. {WARRANTY_LINE}{' '}
              {SHIPPING_LINE}
            </p>
          </div>
          <a href="#pack-single" onClick={event => {
          event.preventDefault();
          requestOffer('single');
        }} className="inline-flex shrink-0 items-center justify-center gap-2 border-2 border-grit-500 px-6 py-4 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color,transform] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-326" data-mp="1qcifs07zqa1u">
            See Complete Systems &amp; deals
            <ArrowDownIcon className="h-5 w-5" aria-hidden="true" data-id="element-327" data-mp="2g2hwaajlze0" />
          </a>
        </div>
      </div>

      <BuilderSummaryBar builder={builder} visible={inView} data-id="element-328" data-mp="3tmlr5l0qdha7" />
      <HardCaseDownsell open={builder.downsellOpen} total={builder.total} onAccept={builder.acceptDownsell} onDecline={builder.declineDownsell} onClose={builder.closeDownsell} data-id="element-329" data-mp="bdcx26qhe8o7" />
    </section>;
}